import React, { useEffect, useState, useRef } from "react";
import "./FloatingUnpaidOrders.css";

const API_BASE = process.env.REACT_APP_API_BASE || "https://api.goharesadaf.ir";
const POLL_INTERVAL_MS = 15000; // 10s - adjust as needed
const MAX_LIST_ITEMS = 6;

export default function FloatingUnpaidOrders() {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [badgePulse, setBadgePulse] = useState(false); // to trigger pulse animation on update
  const [open, setOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const token = localStorage.getItem("token");
  const mountedRef = useRef(true);
  const ariaRef = useRef(null);

  // graceful / safe fetch wrapper
  const safeFetch = async (url, options = {}) => {
    try {
      const res = await fetch(url, options);
      if (!res.ok) {
        const text = await res.text().catch(() => null);
        throw new Error(text || `HTTP ${res.status}`);
      }
      const json = await res.json();
      return json;
    } catch (err) {
      console.error("FloatingUnpaidOrders fetch error:", err);
      throw err;
    }
  };

  // fetch count
  const fetchCount = async () => {
    if (!token) {
      setCount(0);
      return;
    }

    try {
      setLoading(true);
      const data = await safeFetch(`${API_BASE}/api/unpaid-count`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const newCount = Number(data.count || 0);
      if (mountedRef.current) {
        if (typeof newCount === "number" && newCount !== count) {
          setBadgePulse(true);
          // small timeout to clear pulse after animation
          window.setTimeout(() => setBadgePulse(false), 1200);
        }
        setCount(newCount);
        // announce changes for screen readers
        if (ariaRef.current) {
          ariaRef.current.textContent = newCount > 0 ? `شما ${newCount} سفارش پرداخت‌نشده دارید` : "سفارشی پرداخت‌نشده ندارید";
        }
      }
    } catch (err) {
      // ignore silently (we don't want to spam console in production)
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  // fetch list of unpaid orders (on demand)
  const fetchList = async () => {
    if (!token) return;
    setListLoading(true);
    setListError(null);
    try {
      const data = await safeFetch(`${API_BASE}/api/unpaid`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (mountedRef.current) {
        setOrders(Array.isArray(data.orders) ? data.orders.slice(0, MAX_LIST_ITEMS) : []);
      }
    } catch (err) {
      if (mountedRef.current) {
        setListError("خطا در بارگذاری سفارش‌ها");
        setOrders([]);
      }
    } finally {
      if (mountedRef.current) setListLoading(false);
    }
  };

  // initial + polling
  useEffect(() => {
    mountedRef.current = true;
    fetchCount();
    const id = setInterval(fetchCount, POLL_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  // open toggler - fetch list on open
  const toggleOpen = () => {
    if (!token) {
      // redirect to login if not logged in
      window.location.href = `/auth?redirect=${encodeURIComponent(window.location.pathname)}`;
      return;
    }
    const newOpen = !open;
    setOpen(newOpen);
    if (newOpen && orders.length === 0) {
      fetchList();
    }
  };

  if (!token || (count === 0 && !open)) return null;

  return (
    <>
      {/* ARIA live region (visually hidden) */}
      <span aria-live="polite" aria-atomic="true" ref={ariaRef} className="visually-hidden" />

      <div
        className={`floating-unpaid modern ${open ? "open" : ""}`}
        onClick={toggleOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") toggleOpen();
          if (e.key === "Escape") setOpen(false);
        }}
        aria-expanded={open}
        aria-haspopup="menu"
        title={count ? `شما ${count} سفارش پرداخت‌نشده دارید` : "هیچ سفارش پرداخت‌نشده‌ای ندارید"}
      >
        <div className={`floating-unpaid-left ${badgePulse ? "pulse" : ""}`}>
          <div className={`floating-unpaid-badge ${loading ? "loading" : ""}`} aria-hidden>
            {!loading ? (
              <span className="badge-number">{count.toLocaleString("fa-IR")}</span>
            ) : (
              <svg className="badge-spinner" viewBox="0 0 50 50" width="18" height="18" aria-hidden>
                <circle cx="25" cy="25" r="20" stroke="white" strokeWidth="4" strokeLinecap="round" fill="none" strokeDasharray="90" strokeDashoffset="20"/>
              </svg>
            )}
          </div>
        </div>

        <div className="floating-unpaid-body">
          <div className="floating-unpaid-title">سفارش پرداخت‌نشده</div>
          <div className="floating-unpaid-sub">شما {count.toLocaleString("fa-IR")} سفارش پرداخت نشده دارید!</div>
        </div>

        <div className="floating-unpaid-chevron" aria-hidden>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M8 9l4 4 4-4" stroke="#0f172a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        {/* dropdown */}
        <div className={`floating-dropdown ${open ? "visible" : ""}`} role="menu" aria-label="لیست سفارش‌های پرداخت‌نشده">
          <div className="dropdown-header">
            <strong>سفارش‌های پرداخت‌نشده</strong>
            <a className="dropdown-link" href="/orders">مشاهده همه</a>
          </div>

          <div className="dropdown-body">
            {listLoading && <div className="list-loading">در حال بارگذاری...</div>}
            {listError && <div className="list-error">{listError}</div>}
            {!listLoading && !listError && orders.length === 0 && (
              <div className="list-empty">هیچ سفارش پرداخت‌نشده‌ای یافت نشد.</div>
            )}
            <ul className="orders-list">
              {orders.map((o) => (
                <li key={o.id} className="orders-item" onClick={() => window.location.href = `/orders/${o.id}`}>
                  <div className="item-left">
                    <div className="item-id">شمارۀ سفارش: {o.id.toLocaleString("fa-IR")}</div>
                    <div className="item-date">{o.created_at ? new Date(o.created_at).toLocaleString("fa-IR") : ""}</div>
                  </div>
                  <div className="item-right">
                    <div className="item-amount">{Number(o.total ?? o.amount ?? 0).toLocaleString("fa-IR")} تومان</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="dropdown-footer">
            <button className="btn-primary" onClick={() => window.location.href = "/orders?filter=unpaid"}>مدیریت سفارش‌ها</button>
            <button className="btn-quiet" onClick={() => setOpen(false)}>بستن</button>
          </div>
        </div>
      </div>
    </>
  );
}
